import React from 'react';
import { Briefcase, Coffee, CheckCircle, RefreshCw, AlertTriangle } from 'lucide-react';
import CommonFields from './CommonFields';

const InsertEntryForm = ({
  editMode, insertDate, setInsertDate,
  insertStartTime, setInsertStartTime,
  insertEndTime, setInsertEndTime,
  reason, setReason, evidenceFile, evidenceUrl,
  fileInputRef, onFileUpload, submitting,
  onSubmit, onCancel,
  insertConflict
}) => {
  const isWork = editMode === 'insert_work';

  return (
    <div className={`bg-slate-800/50 rounded-2xl border p-6 ${
      isWork ? 'border-blue-500/30' : 'border-emerald-500/30'
    }`}>
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        {isWork ? (
          <><Briefcase className="w-5 h-5 text-blue-400" /> Insert Missed Work Time</>
        ) : (
          <><Coffee className="w-5 h-5 text-emerald-400" /> Insert Missed Rest Break</>
        )}
      </h3>

      <div className="space-y-4">
        <div className={`p-3 rounded-lg ${isWork ? 'bg-blue-500/10' : 'bg-emerald-500/10'}`}>
          <p className="text-slate-300 text-sm">
            {isWork
              ? 'This will insert a work period into an existing rest segment, splitting it into REST → WORK → REST.'
              : 'This will insert a rest break into an existing work period, splitting it into WORK → REST → WORK.'
            }
          </p>
        </div>

        <div>
          <label className="block text-sm text-slate-400 mb-1">Date</label>
          <input
            type="date"
            value={insertDate}
            onChange={(e) => setInsertDate(e.target.value)}
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white cursor-pointer [color-scheme:dark]"
            data-testid="insert-date"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">
              {isWork ? 'Work' : 'Rest'} Started At
            </label>
            <input
              type="time"
              value={insertStartTime}
              onChange={(e) => setInsertStartTime(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white [color-scheme:dark]"
              data-testid="insert-start-time"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">
              {isWork ? 'Work' : 'Rest'} Ended At
            </label>
            <input
              type="time"
              value={insertEndTime}
              onChange={(e) => setInsertEndTime(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white [color-scheme:dark]"
              data-testid="insert-end-time"
            />
          </div>
        </div>

        {/* Duration preview with cross-midnight support */}
        {insertStartTime && insertEndTime && (
          <div className="p-3 bg-slate-700/50 rounded-lg">
            <p className="text-slate-400 text-sm">
              Duration: {(() => {
                const [sh, sm] = insertStartTime.split(':').map(Number);
                const [eh, em] = insertEndTime.split(':').map(Number);
                let mins = (eh * 60 + em) - (sh * 60 + sm);
                const crossesMidnight = mins <= 0;
                if (crossesMidnight) mins += 24 * 60;
                const hrs = Math.floor(mins / 60);
                const m = mins % 60;
                return `${hrs > 0 ? `${hrs}h ` : ''}${m}m${crossesMidnight ? ' (crosses midnight → next day)' : ''}`;
              })()}
            </p>
          </div>
        )}

        {/* Insert conflict warning */}
        {insertConflict && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-start gap-2" data-testid="insert-conflict-warning">
            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
            <p className="text-red-300 text-sm">{insertConflict}</p>
          </div>
        )}

        <CommonFields
          reason={reason} setReason={setReason}
          evidenceFile={evidenceFile} evidenceUrl={evidenceUrl}
          fileInputRef={fileInputRef} onFileUpload={onFileUpload}
        />

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
            data-testid="cancel-insert-btn"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={submitting || !insertStartTime || !insertEndTime || !reason || !!insertConflict}
            className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 flex items-center gap-2 ${
              isWork ? 'bg-blue-500 hover:bg-blue-600' : 'bg-emerald-500 hover:bg-emerald-600'
            }`}
            data-testid="save-insert-btn"
          >
            {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            Insert & Recalculate
          </button>
        </div>
      </div>
    </div>
  );
};

export default InsertEntryForm;
